/**
 * The right-hand panel — the in-studio playtest terminal.
 *
 * Originally a tabbed Inspect / Play system, but scene editing
 * moved fully inline (the floating card on the graph) and
 * adventure-level config moved to its own settings modal, so
 * the panel is now Play-only: a header strip + the Terminal.
 *
 * Drag-resizable from its left edge; width persists to
 * localStorage so the author's preferred size survives a
 * refresh.
 */
import { Terminal, type PlayState } from './Terminal';
import { PANEL, PANEL_BORDER, PHOSPHOR, AMBER } from '../lib/theme';
import { useResizable } from '../lib/useResizable';
import type { AdventureJson } from 'console-adventure';

interface Props {
	json: AdventureJson;
	jsonVersion: number;
	playFrom: string | null;
	playRequestId: number;
	onPlayStateChange: (s: PlayState) => void;
	onClearPlayFrom: () => void;
}

export function RightPanel({
	json,
	jsonVersion,
	playFrom,
	playRequestId,
	onPlayStateChange,
	onClearPlayFrom
}: Props) {
	const { width, dragging, onMouseDown } = useResizable({
		initial: 420,
		min: 320,
		max: 900
	});

	return (
		<aside
			data-tour="right-panel"
			style={{
				width,
				flexShrink: 0,
				background: PANEL,
				borderLeft: `1px solid ${PANEL_BORDER}`,
				color: '#eef0f5',
				fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
				position: 'relative'
			}}
		>
			{/* Drag handle on the very left edge of the panel. The
			    handle straddles the border (left: -4) so the user
			    has a 6px-wide hit area to grab. */}
			<div
				onMouseDown={onMouseDown}
				style={{
					position: 'absolute',
					top: 0,
					bottom: 0,
					left: -4,
					width: 8,
					cursor: 'col-resize',
					background: dragging ? AMBER : 'transparent',
					transition: dragging ? 'none' : 'background 120ms',
					zIndex: 20
				}}
				onMouseEnter={(e) => {
					if (!dragging) e.currentTarget.style.background = `${AMBER}55`;
				}}
				onMouseLeave={(e) => {
					if (!dragging) e.currentTarget.style.background = 'transparent';
				}}
				aria-label="resize panel"
				role="separator"
			/>

			{/* Header strip — labels the panel now that there's no
			    tab bar. */}
			<div
				style={{
					padding: '12px 14px',
					borderBottom: `1px solid ${PANEL_BORDER}`,
					background: PANEL,
					color: PHOSPHOR,
					fontSize: 11,
					fontWeight: 700,
					letterSpacing: '0.1em'
				}}
			>
				▶ PLAYTEST
			</div>

			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					overflow: 'hidden'
				}}
			>
				<Terminal
					json={json}
					jsonVersion={jsonVersion}
					playFrom={playFrom}
					playRequestId={playRequestId}
					onClearPlayFrom={onClearPlayFrom}
					onStateChange={onPlayStateChange}
				/>
			</div>
		</aside>
	);
}
